import {
  DEFAULT_DURATION_MINUTES,
  type DurationMinutes,
} from '../games/types'

export const WARNING_REMAINING_MS = 60_000

export type TimerPhase = 'idle' | 'running' | 'paused' | 'winddown'

export type TimerSnapshot = {
  phase: TimerPhase
  remainingMs: number
  durationMs: number
  warning: boolean
}

export class SessionTimer {
  private phase: TimerPhase = 'idle'
  private durationMs: number
  private remainingMs: number
  private lastTickAt: number | null = null

  constructor(durationMin: DurationMinutes = DEFAULT_DURATION_MINUTES) {
    this.durationMs = minutesToMs(durationMin)
    this.remainingMs = this.durationMs
  }

  snapshot(): TimerSnapshot {
    return {
      phase: this.phase,
      remainingMs: this.remainingMs,
      durationMs: this.durationMs,
      warning: isWarning(this.phase, this.remainingMs),
    }
  }

  enterGame(now: number): TimerSnapshot {
    if (this.phase === 'winddown') return this.snapshot()
    if (this.phase === 'idle' || this.phase === 'paused') {
      this.phase = 'running'
      this.lastTickAt = now
    }
    return this.snapshot()
  }

  leaveToHome(now: number): TimerSnapshot {
    if (this.phase === 'running') {
      this.tick(now)
      if (this.phase === 'running') {
        this.phase = 'paused'
        this.lastTickAt = null
      }
    }
    return this.snapshot()
  }

  tick(now: number): TimerSnapshot {
    if (this.phase !== 'running' || this.lastTickAt === null) {
      return this.snapshot()
    }
    const elapsed = Math.max(0, now - this.lastTickAt)
    this.lastTickAt = now
    this.remainingMs = Math.max(0, this.remainingMs - elapsed)
    if (this.remainingMs <= 0) {
      this.remainingMs = 0
      this.phase = 'winddown'
      this.lastTickAt = null
    }
    return this.snapshot()
  }

  setDurationMinutes(minutes: DurationMinutes): TimerSnapshot {
    this.durationMs = minutesToMs(minutes)
    if (this.phase === 'idle') {
      this.remainingMs = this.durationMs
    } else if (this.phase !== 'winddown') {
      this.remainingMs = Math.min(this.remainingMs, this.durationMs)
    }
    return this.snapshot()
  }

  reset(): TimerSnapshot {
    this.phase = 'idle'
    this.remainingMs = this.durationMs
    this.lastTickAt = null
    return this.snapshot()
  }
}

function minutesToMs(minutes: DurationMinutes): number {
  return minutes * 60_000
}

function isWarning(phase: TimerPhase, remainingMs: number): boolean {
  return (
    (phase === 'running' || phase === 'paused') &&
    remainingMs > 0 &&
    remainingMs <= WARNING_REMAINING_MS
  )
}
